import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import { Loader2, CheckCircle, XCircle, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

const VerifyMagicLink = () => {
  const [searchParams] = useSearchParams();
  const { user: contextUser, setUser } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("verifying");
  const [errorMessage, setErrorMessage] = useState("");
  const [pendingDest, setPendingDest] = useState(null);
  const [localUser, setLocalUser] = useState(null); // Local user state for immediate redirect
  const hasProcessed = useRef(false);

  // Navigate ONLY after user is confirmed — prevents race condition
  // Use localUser which is set synchronously with pendingDest
  useEffect(() => {
    if (pendingDest && (localUser || contextUser)) {
      const timer = setTimeout(() => navigate(pendingDest, { replace: true }), 600);
      return () => clearTimeout(timer);
    }
  }, [pendingDest, localUser, contextUser, navigate]);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const verifyToken = async () => {
      const token = searchParams.get("token");

      if (!token) {
        setStatus("error");
        setErrorMessage("Token não encontrado no link.");
        return;
      }

      try {
        const response = await axios.post(
          `${API}/auth/verify-magic-link`,
          { token },
          { withCredentials: true }
        );

        const { user, session_token, redirect } = response.data;
        
        if (session_token) {
          // Use sessionStorage instead of localStorage for better security (XSS mitigation)
          // sessionStorage is cleared when the browser tab/window is closed
          sessionStorage.setItem("session_token", session_token);
          axios.defaults.headers.common["Authorization"] = `Bearer ${session_token}`;
        }
        
        setUser(user);
        setLocalUser(user); // Set local user immediately for redirect to work
        setStatus("success");
        toast.success(`Bem-vindo, ${user.name}!`);

        // If there's a redirect URL from the magic link, use it
        // Otherwise, redirect based on role
        let dest;
        if (redirect) {
          dest = redirect;
        } else {
          dest = user.role === "admin" ? "/admin" : user.role === "professor" ? "/professor" : "/dashboard";
        }
        setPendingDest(dest);
      } catch (error) {
        console.error("Magic link verification failed:", error);
        console.error("Response data:", error.response?.data);
        console.error("Response status:", error.response?.status);
        setStatus("error");
        const detail = error.response?.data?.detail;
        const statusCode = error.response?.status;
        let msg = "Erro ao verificar o link. Por favor tenta novamente.";
        if (detail) {
          msg = detail;
        } else if (statusCode === 0 || !error.response) {
          msg = "Erro de ligação. Verifica a tua internet ou tenta novamente.";
        } else if (statusCode === 500) {
          msg = "Erro no servidor. Tenta novamente mais tarde.";
        }
        setErrorMessage(msg);
      }
    };

    verifyToken();
  }, [searchParams, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f5f5f5] px-4">
      <div className="text-center max-w-md">
        <img
          src="/assets/images/logo.png"
          alt="Apúlia Surf School"
          className="h-20 w-auto mx-auto mb-8"
        />

        {status === "verifying" && (
          <>
            <Loader2 className="h-16 w-16 text-[#6697a4] animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-[#3d5a63] mb-2">A verificar...</h2>
            <p className="text-gray-500">Por favor aguarda um momento</p>
          </>
        )}

        {status === "success" && (
          <>
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-[#3d5a63] mb-2">Autenticado com sucesso!</h2>
            <p className="text-gray-500">A redirecionar...</p>
          </>
        )}

        {status === "error" && (
          <>
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-[#3d5a63] mb-2">Erro na verificação</h2>
            <p className="text-gray-500 mb-6">{errorMessage}</p>
            <div className="space-y-3">
              <Link to="/">
                <Button className="w-full bg-[#6697a4] hover:bg-[#3d5a63]">
                  Pedir novo link de acesso
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Voltar ao início
                </Button>
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyMagicLink;
